import { useCurrentUser } from "@/store/useUserStore";
import { RoleName } from "../../domain/models/User";

const CREATE_REQUEST_PERMISSIONS = [
  "create requests",
  "create client requests",
  "create subsidiary requests",
];

const UPDATE_REQUEST_PERMISSIONS = [
  "update requests",
  "update client requests",
  "update subsidiary requests",
];

const DELETE_REQUEST_PERMISSIONS = [
  "delete requests",
  "delete assigned requests",
];

export function usePermissions() {
  const currentUser = useCurrentUser();

  const getAllPermissions = (): string[] => {
    if (!currentUser?.roles) return [];
    return currentUser.roles
      .flatMap((role) => role.permissions)
      .map((p) => p.name as string);
  };

  const hasPermission = (permission: string): boolean =>
    getAllPermissions().includes(permission);

  const hasAnyPermission = (permissions: string[]): boolean =>
    permissions.some((permission) => hasPermission(permission));

  const canCreateRequest = (): boolean =>
    hasAnyPermission(CREATE_REQUEST_PERMISSIONS);

  const canUpdateRequest = (): boolean =>
    hasAnyPermission(UPDATE_REQUEST_PERMISSIONS);

  const canDeleteRequest = (): boolean =>
    hasAnyPermission(DELETE_REQUEST_PERMISSIONS);

  const hasRole = (roles: RoleName[]): boolean =>
    currentUser?.roles?.some((r) => roles.includes(r.name)) ?? false;

  return {
    getAllPermissions,
    hasPermission,
    hasAnyPermission,
    canCreateRequest,
    canUpdateRequest,
    canDeleteRequest,
    hasRole,
  };
}
